"use client";

import React, { useMemo } from 'react';
import clsx from 'clsx';
import { isFilled } from '@prismicio/helpers';
import { Settings } from '@/app/lib/settings';
import { ConditionalLink } from '@/app/lib/ConditionalLink';
import { PrismicNextImage } from '@prismicio/next';
import { MoreLink } from '@/app/lib/MoreLink';
import Chevron from '@/app/assets/images/icon_chevron.svg';
import styles from './highlightsList.module.scss';
import sharedStyles from '@/app/shared.module.scss';
import { useRouter, usePathname } from 'next/navigation';

export default function HighlightsList({ slice, highlights, pageData }) {
	const router = useRouter();
	const pathname = usePathname();

	const basePrefix = useMemo(() => {
		return pageData?.type === 'event'
			? `/${Settings.eventsPrefix}/${pageData?.uid}/highlights`
			: '/highlights';
	}, [pageData]);

	const segments = pathname.split('/').filter(Boolean);
	const rawFilterSlug = segments.length > 0 ? segments[segments.length - 1] : '';

	const slugToFilterValue = (slug) => {
		const entry = Object.entries(Settings.highlightsTypes).find(([key]) => key === slug);
		return entry ? entry[1] : '';
	};

	const currentFilterValue = useMemo(() => {
		if (Object.keys(Settings.highlightsTypes).includes(rawFilterSlug)) {
			return slugToFilterValue(rawFilterSlug);
		}
		return '';
	}, [rawFilterSlug]);


	const toggleFilter = (filterSlug, filterValue) => {
		let newPath = basePrefix;

		const isTogglingOff = filterValue === 'All' || currentFilterValue === filterValue;

		if (!isTogglingOff) {
			newPath = `${basePrefix}/${filterSlug}`;
		}

		router.replace(newPath, { shallow: true });
	};

	const filteredHighlights = useMemo(() => {
		if (!currentFilterValue || currentFilterValue === 'All') {
			return highlights;
		}

		return highlights?.filter((highlight) => {
			return highlight?.data?.type === currentFilterValue;
		});
	}, [highlights, currentFilterValue]);

	return (
		<div className={clsx(styles.wrapper)}>

			{/* filters */}
			{slice?.primary?.show_filter && (
				<div className={clsx(sharedStyles.filters)}>
					<div className={clsx(sharedStyles.label_wrap)}>
						<div className={clsx(sharedStyles.label)}>
							Filter
						</div>

						<Chevron className={sharedStyles.chevron} />
					</div>

					{Object.entries(Settings.highlightsTypes).map(([filterSlug, filterValue]) => {
						const isHighlighted = currentFilterValue === filterValue;
						return (
							<span
								key={filterSlug}
								onClick={() => toggleFilter(filterSlug, filterValue)}
								className={clsx(sharedStyles.item, { [sharedStyles.highlighted]: isHighlighted })}
							>
								{filterValue}
							</span>
						)
					})}
				</div>
			)}

			{/* grid */}
			{filteredHighlights?.length ? (
				<div className={clsx(styles.main)}>
					{filteredHighlights.map((highlight, index) => {
						return (
							<ConditionalLink
								url={highlight?.data?.event?.uid ? `/${Settings.eventsPrefix}/${highlight?.data?.event?.uid}/highlights/${highlight?.uid}` : `/highlights/${highlight?.uid}`} className={clsx(styles.highlight)} key={index}>

								{isFilled.image(highlight?.data?.image) && (
									<div className={clsx(styles.image_wrapper)}>
										<PrismicNextImage field={highlight?.data?.image} fallbackAlt='' className={clsx(styles.image)} />
									</div>
								)}

								{isFilled.keyText(highlight?.data?.title) && (
									<h3 className={clsx(styles.highlight_title)}>{highlight?.data?.title}</h3>
								)}

							</ConditionalLink>
						)
					})}
				</div>
			) : (
				<div className={styles.noResults}>No highlight found.</div>

			)}

			{/* back link */}
			{pageData?.type === 'event' && (
				<MoreLink link={`/${Settings.eventsPrefix}/${pageData?.uid}`} text={"Back to event"} slice={slice} back={true} />
			)}

		</div>
	);
}